import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommandBase } from '../../../model/command';
import { ColumnDefinition } from '../../../model/column-definition';
import ListViewColumn, { ListColumn } from '../../../model/list-view-column';
import { ListRow, ListViewRow } from '../../../model/list-view-row';
import { MenuItemDefinition } from '../../../model/menu-item-definition';
import { ModelBase } from '../../../model/model-base';
import { CommandDispatcher } from '../../../service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../service/factory/command-factory.service';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { MessageService } from '../../../service/message.service';
import { ListViewHeaderComponent } from '../list-view-header/list-view-header.component';
import { SortEvent, State, StateChangeEvent } from '../list-view-header/list-view-header.interface';
import { DefaultPageSize, PagingEvent } from '../paging/paging.component';
import { LanguageService } from '../../../service/language.service';

@Component({
    selector: 'app-paged-list-view',
    templateUrl: './paged-list-view.component.html',
    styleUrls: ['./paged-list-view.component.scss', './../list-view/list-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PagedListViewComponent<TSource extends { id: string } | ModelBase = ModelBase> implements OnInit, AfterViewChecked {
  constructor(
    protected columnFactory: ListViewColumnFactory,
    protected languageService: LanguageService,
    protected commandDispatcher: CommandDispatcher,
    protected commandFactory: CommandFactoryService,
    protected messageService: MessageService,
    private cd: ChangeDetectorRef
  ) {
    this.subscription = new Subscription();
    this.subscription.add(
      languageService.onLangChange.subscribe(e => {
        this._columns.forEach(col => (col.locale = e.lang));
        this._rows = [];
        this._summaryValuesCache.clear();
      })
    );
    this.selectAllControl = new FormControl();
    this.subscription.add(
      this.selectAllControl.valueChanges.subscribe(value => {
        this.rows.filter(r => r.selected !== value).forEach(i => (i.selected = value));
        if (value) {
          if (this.rows.length === 0) {
            this.selectAllControl.setValue(false);
            return;
          }
          this.returnToAllSelected = true;
          this.selectedItemsCount = this.totalCount;
        } else {
          this.returnToAllSelected = false;
          this.selectedItemsCount = 0;
        }
        this.selectedIds = [];
        this.deSelectedIds = [];
        this.onAllSelectedChange.emit(this.returnToAllSelected);
        this.onAffectedItemsCountChange.emit(this.selectedItemsCount);
        this.onSelectedIdsChange.emit(this.selectedIds);
        this.onDeselectedIdsChange.emit(this.deSelectedIds);
        this.cd.detectChanges();
      })
    );
  }

  @Input() columnDefinition: ColumnDefinition<TSource>[];
  @Input() contextMenuDefinition: MenuItemDefinition[];
  @Input() totalCount: number;
  @Input() isToolbarSticky: boolean = false;
  @Input() preferencesKey: string;
  // Multiselect start

  @Input() menuDefinition: MenuItemDefinition[];
  @Input() externalMenuCommandIds: string[] = [];
  @Output() menuCommand = new EventEmitter<CommandBase>();
  @Input() set selectAll(value: boolean) {
    this.selectAllControl.setValue(value);
  }

  get multiselect(): boolean {
    return this.menuDefinition && this.menuDefinition.length > 0;
  }
  selectAllControl: FormControl;

  deSelectedIds: string[] = [];
  selectedIds: string[] = [];
  returnToAllSelected: boolean;
  selectionChanged(checked: boolean, row: ListRow<TSource>) {
    row.selected = checked;

    if (this.returnToAllSelected) {
      if (checked) {
        let index = this.deSelectedIds.indexOf(row.dataSource.id);
        if (index >= 0) {
          this.deSelectedIds.splice(index, 1);
        }
      } else {
        if (!this.deSelectedIds.find(id => id === row.dataSource.id)) {
          this.deSelectedIds.push(row.dataSource.id);
        }
      }
      this.selectAllControl.setValue(this.deSelectedIds.length === 0, { emitEvent: false });
      this.selectedItemsCount = this.totalCount - this.deSelectedIds.length;
    } else {
      if (checked) {
        if (!this.selectedIds.find(id => id === row.dataSource.id)) {
          this.selectedIds.push(row.dataSource.id);
        }
      } else {
        let index = this.selectedIds.indexOf(row.dataSource.id);
        if (index >= 0) {
          this.selectedIds.splice(index, 1);
        }
      }
      this.selectedItemsCount = this.selectedIds.length;
      this.selectAllControl.setValue(false, { emitEvent: false });
    }
    this.onSelectedIdsChange.emit(this.selectedIds);
    this.onDeselectedIdsChange.emit(this.deSelectedIds);
    this.onAffectedItemsCountChange.emit(this.selectedItemsCount);
    this.onAllSelectedChange.emit(this.returnToAllSelected);
    this.cd.detectChanges();
    if (this.selectedItemsCount == 0) {
      this.showMenu = false;
    }
  }

  execute(commandId: string) {
    this.showMenu = false;
    if (this.selectedItemsCount === 0) {
      this.messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
      return;
    }
    const command = this.commandFactory.createFilteredCommand(commandId, this.deSelectedIds, this.selectedIds, this.selectedItemsCount);
    if (this.externalMenuCommandIds?.includes(commandId)) {
      this.menuCommand.emit(command);
    } else {
      this.commandDispatcher.sendCommand(command);
    }
  }

  showMenu: boolean;

  private cancelHide = false;

  onMenuMouseEnter(): void {
    this.showMenu = this.selectedItemsCount !== 0;
    this.cancelHide = true;
  }

  onMenuMouseLeave(): void {
    this.cancelHide = false;
    setTimeout(() => {
      if (!this.cancelHide) {
        this.showMenu = false;
      }
    }, 200);
  }

  openMenu() {
    if (this.selectedItemsCount > 0) this.showMenu = !this.showMenu;
  }

  selectedItemsCount: number;

  // Multiselect end

  @Output() rowClick = new EventEmitter<{ context: TSource; event: PointerEvent }>();
  @Output() onPageChange = new EventEmitter<PagingEvent>();
  @Output() onSortChange = new EventEmitter<SortEvent>();
  @Output() onColumnChange = new EventEmitter<ListViewColumn<TSource>[]>();
  @Output() onSelectedIdsChange = new EventEmitter<string[]>();
  @Output() onDeselectedIdsChange = new EventEmitter<string[]>();
  @Output() onAffectedItemsCountChange = new EventEmitter<number>();
  @Output() onAllSelectedChange = new EventEmitter<boolean>();
  @ViewChild('scrollView') scrollView: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarScrollView') toolbarScrollView: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarScrollContent') toolbarScrollContent: ElementRef<HTMLDivElement>;
  @ViewChild('tableView') tableView: ElementRef<HTMLTableElement>;
  @ViewChildren(ListViewHeaderComponent) headers: QueryList<ListViewHeaderComponent>;
  @Input() set itemsSource(value: TSource[]) {
    this.page = this._pageAfterReload;
    this.pageSize = this._pageSizeAfterReload;
    this._itemsSource = value;
    this._rows = [];
    this._summaryValuesCache.clear();
    if (value?.length > 0) {
      const modelItem = value[0] as ModelBase;
      this.recentId = modelItem?.consumeRecentId?.() || undefined;
    }
  }
  private _itemsSource: TSource[];
  get itemsSource(): TSource[] {
    return this._itemsSource;
  }

  private _columns: ListViewColumn<TSource>[];
  private _rows: ListViewRow<TSource>[] = [];
  private readonly _emptySummaryValues: { displayValue: any }[] = [];
  private readonly _summaryValuesCache = new Map<
    string,
    {
      rows: ListViewRow<TSource>[];
      values: {
        displayValue: any;
        value: any;
      }[];
    }
  >();
  protected recentId: string = undefined; // used to highlight recently viewed item
  protected subscription: Subscription;

  private _pageAfterReload = 1;
  private _pageSizeAfterReload = DefaultPageSize;
  page = 1;
  pageSize = DefaultPageSize;
  private scrollSyncInProgress = false;
  private toolbarScrollContentWidth = 0;

  ngOnInit() {}

  ngAfterViewChecked() {
    this.updateToolbarScrollWidth();
  }

  getRowNumber(index: number): number {
    let fromIndex = (this.page - 1) * this.pageSize;
    return fromIndex + index + 1;
  }

  rememberSelectionOnDataChanged = false;

  pagingChanged(e: PagingEvent) {
    this._pageAfterReload = e.pageNumber;
    this._pageSizeAfterReload = e.itemsPerPage;
    if (this.page !== this._pageAfterReload || this.pageSize !== this._pageSizeAfterReload) {
      this.rememberSelectionOnDataChanged = true;
      this.page = this._pageAfterReload;
      this.pageSize = this._pageSizeAfterReload;
    }
    this.onPageChange.emit(e);
  }

  get columns(): ListViewColumn<TSource>[] {
    if (!this._columns && this.columnDefinition) {
      this._columns = this.columnFactory.create(this.columnDefinition);
      this.onColumnChange.emit(this._columns);
    }
    return this._columns;
  }

  ColumnChanged(columns?: ListViewColumn<TSource>[]) {
    if (columns) {
      this._columns = columns;
    }
    this.onColumnChange.emit(this._columns);
    this.cd.detectChanges();
  }

  selectedIdsChanged() {
    this.onSelectedIdsChange.emit(this.selectedIds);
  }

  deSelectedIdsChanged() {
    this.onDeselectedIdsChange.emit(this.deSelectedIds);
  }

  affectedItemsCountChanged() {
    this.onAffectedItemsCountChange.emit(this.selectedItemsCount);
  }
  allSelectedChanged() {
    this.onAllSelectedChange.emit(this.returnToAllSelected);
  }

  get rows(): ListViewRow<TSource>[] {
    if (this.itemsSource && this._rows.length !== this.itemsSource.length) {
      this._rows = this.itemsSource.map(x => (x instanceof ListViewRow ? x : new ListViewRow(x)));
      this._summaryValuesCache.clear();

      if (this.recentId) {
        this._rows.forEach(x => (x.highlight = x.dataSource?.id === this.recentId));
      }
      if (this.rememberSelectionOnDataChanged) {
        if (this.selectAllControl.value) {
          this.rows.forEach(r => (r.selected = true));
        } else {
          if (this.returnToAllSelected) {
            for (let row of this._rows) {
              row.selected = !this.deSelectedIds.find(id => id === row.dataSource.id);
            }
          } else {
            this._rows.forEach(r => (r.selected = this.selectedIds.find(id => id === r.dataSource.id) !== undefined));
          }
        }
        this.rememberSelectionOnDataChanged = false;
      } else {
        this.selectAllControl.setValue(false);
      }
      if (this.headers) {
        this.headers.map(h => h.updateState());
      }
    }
    return this._rows;
  }

  get totalCountPlaceholder(): string {
    return this.totalCount === 1 ? 'COMMON.ITEM' : 'COMMON.ITEMS';
  }

  get selectedColumns(): ListViewColumn<TSource>[] {
    const selected = this._columns ? this._columns.filter(x => x.selected) : [];
    return selected;
  }

  get hasContextMenu(): boolean {
    return this.contextMenuDefinition && this.contextMenuDefinition.length > 0;
  }

  get isEmpty(): boolean {
    return this._itemsSource && this._rows.length === 0;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscription = null;
  }

  onToolbarScroll() {
    if (this.scrollSyncInProgress || !this.scrollView || !this.toolbarScrollView) return;

    this.scrollSyncInProgress = true;
    this.scrollView.nativeElement.scrollLeft = this.toolbarScrollView.nativeElement.scrollLeft;
    this.scrollSyncInProgress = false;
  }

  onTableScroll() {
    if (this.scrollSyncInProgress || !this.scrollView || !this.toolbarScrollView) return;

    this.scrollSyncInProgress = true;
    this.toolbarScrollView.nativeElement.scrollLeft = this.scrollView.nativeElement.scrollLeft;
    this.scrollSyncInProgress = false;
  }

  private updateToolbarScrollWidth() {
    if (!this.tableView || !this.toolbarScrollContent) {
      this.toolbarScrollContentWidth = 0;
      return;
    }

    const scrollContainer = this.scrollView?.nativeElement;
    const toolbarScroll = this.toolbarScrollView?.nativeElement;
    if (!scrollContainer || !toolbarScroll) return;

    const tableScrollRange = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
    const width = toolbarScroll.clientWidth + tableScrollRange;
    const styleWidth = `${width}px`;
    if (width === this.toolbarScrollContentWidth && this.toolbarScrollContent.nativeElement.style.width === styleWidth) return;

    this.toolbarScrollContentWidth = width;
    this.toolbarScrollContent.nativeElement.style.width = styleWidth;

    if (toolbarScroll.scrollLeft !== scrollContainer.scrollLeft) {
      toolbarScroll.scrollLeft = scrollContainer.scrollLeft;
    }
  }

  onRowClicked(row: ListViewRow<TSource>, event: PointerEvent) {
    (row.dataSource as ModelBase)?.storeRecentId?.(); // so we can highlight it when the user navigates back
    this.rowClick.emit({ context: row.dataSource, event: event });
  }

  private _currentSort: string;
  private _state: State;

  allowSort(column: ListViewColumn): boolean {
    return column.canSort;
  }

  onStateChange(e: StateChangeEvent) {
    const column = this.selectedColumns.find(x => x.id === e.payload.id) as ListViewColumn<TSource>;
    if (!column.canSort) {
      return;
    }

    if (column.binding === this._currentSort && e.payload.state === this._state) {
      return;
    }

    this._currentSort = column.binding;
    this._state = e.payload.state;

    if (this.headers) {
      this.headers.filter(x => x.id !== e.payload.id).forEach(x => x.toDefault());
    }

    if (e.payload.state == State.DEFAULT || this.pageSize < this.totalCount) {
      this._rows = [];
      this._summaryValuesCache.clear();
    } else {
      this._rows.sort((a, b) => {
        switch (e.payload.state) {
          case State.ORDERBYASC:
            return a.compareByColumn(b, column);

          case State.ORDERBYDESC:
            return b.compareByColumn(a, column);
        }
      });
      this._summaryValuesCache.clear();
    }

    this.onSortChange.emit({ column: column, state: e.payload.state });
  }

  trackRow(index: number, row: ListViewRow<TSource>): string | number {
    const id = row?.dataSource?.id;
    return id ? `${id}:${index}` : index;
  }

  sumValues(column: ListViewColumn): {
    displayValue: any;
  }[] {
    if (this.page !== 1 || this.totalCount > this.pageSize) {
      return this._emptySummaryValues;
    }

    const rows = this.rows;
    const cacheKey = `${column.id}:${this.page}:${this.totalCount}:${this.pageSize}`;
    const cached = this._summaryValuesCache.get(cacheKey);
    if (cached?.rows === rows) {
      return cached.values;
    }

    let summaries: {
      displayValue: any;
      value: any;
    }[] = [];

    rows.forEach(row => row.addToSummaryValue(summaries, column));
    summaries.forEach(sum => (sum.displayValue = column.converter ? column.converter(sum.value) : sum.value));
    this._summaryValuesCache.set(cacheKey, { rows, values: summaries });
    return summaries;
  }
}
